import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CompanyProfile } from '@/types/companyProfile';

interface ProfileState {
  // Active profile (currently selected)
  activeProfile: CompanyProfile | null;

  // All user profiles
  profiles: CompanyProfile[];

  // Loading states
  isLoadingActive: boolean;
  isLoadingProfiles: boolean;

  // Actions
  setActiveProfile: (profile: CompanyProfile | null) => void;
  setProfiles: (profiles: CompanyProfile[]) => void;
  setLoadingActive: (loading: boolean) => void;
  setLoadingProfiles: (loading: boolean) => void;

  // Helper to update a single profile in the list
  updateProfile: (profileId: string, updates: Partial<CompanyProfile>) => void;

  // Helper to remove a profile from the list
  removeProfile: (profileId: string) => void;

  // Helper to add a new profile
  addProfile: (profile: CompanyProfile) => void;

  // Clear all profile state (on logout)
  clearProfiles: () => void;
}

export const useProfileStore = create<ProfileState>()(
  persist(
    (set, get) => ({
      activeProfile: null,
      profiles: [],
      isLoadingActive: false,
      isLoadingProfiles: false,

      setActiveProfile: (profile) => set({ activeProfile: profile }),

      setProfiles: (profiles) => set({ profiles }),

      setLoadingActive: (loading) => set({ isLoadingActive: loading }),

      setLoadingProfiles: (loading) => set({ isLoadingProfiles: loading }),

      updateProfile: (profileId, updates) => {
        const { profiles, activeProfile } = get();

        // Update in profiles list
        const updatedProfiles = profiles.map((p) =>
          p.id === profileId ? { ...p, ...updates } : p
        );
        set({ profiles: updatedProfiles });

        // Update active profile if it's the one being updated
        if (activeProfile?.id === profileId) {
          set({ activeProfile: { ...activeProfile, ...updates } });
        }
      },

      removeProfile: (profileId) => {
        const { profiles, activeProfile } = get();

        // Remove from profiles list
        const updatedProfiles = profiles.filter((p) => p.id !== profileId);
        set({ profiles: updatedProfiles });

        // Clear active profile if it's the one being removed
        if (activeProfile?.id === profileId) {
          set({ activeProfile: null });
        }
      },

      addProfile: (profile) => {
        const { profiles } = get();
        set({ profiles: [...profiles, profile] });

        // If this is the first profile or it's marked as active, set it as active
        if (profiles.length === 0 || profile.is_active) {
          set({ activeProfile: profile });
        }
      },

      clearProfiles: () => set({
        activeProfile: null,
        profiles: [],
        isLoadingActive: false,
        isLoadingProfiles: false,
      }),
    }),
    {
      name: 'profile-storage',
      // Only persist the active profile and profiles list, not loading states
      partialize: (state) => ({
        activeProfile: state.activeProfile,
        profiles: state.profiles,
      }),
    }
  )
);
